import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useSubmitWaitlistInquiry, type WaitlistReason } from '@/hooks/useWaitlistInquiry'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'

const REASON_OPTIONS = [
  { value: 'urgent_project', label: 'I have an urgent project starting soon' },
  { value: 'questions', label: 'I have questions about AionTrack' },
  { value: 'status_check', label: 'Just checking on my status' },
  { value: 'other', label: 'Other' },
] as const

// Create schema with conditional validation
const inquirySchema = z.object({
  reason: z.enum(['urgent_project', 'questions', 'status_check', 'other'], {
    message: 'Please select a reason',
  }),
  message: z.string().optional(),
}).refine(
  (data) => {
    // Message is required for all reasons except status_check
    if (data.reason === 'status_check') return true
    return data.message && data.message.trim().length > 0
  },
  {
    message: 'Please provide a message',
    path: ['message'],
  }
)

type InquiryFormInput = z.infer<typeof inquirySchema>

interface WaitlistInquiryFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Form for waitlisted users to submit inquiries
 * Opened from WaitlistModal
 */
export function WaitlistInquiryForm({ open, onOpenChange }: WaitlistInquiryFormProps) {
  const [submitted, setSubmitted] = useState(false)
  const submitInquiry = useSubmitWaitlistInquiry()

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<InquiryFormInput>({
    resolver: zodResolver(inquirySchema),
  })

  const selectedReason = watch('reason')

  const onSubmit = async (data: InquiryFormInput) => {
    try {
      await submitInquiry.mutateAsync({
        reason: data.reason as WaitlistReason,
        message: data.message,
      })
      setSubmitted(true)
      // Auto-close after 3 seconds
      setTimeout(() => {
        setSubmitted(false)
        reset()
        onOpenChange(false)
      }, 3000)
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit inquiry')
    }
  }

  const handleClose = () => {
    if (!submitInquiry.isPending) {
      setSubmitted(false)
      reset()
      onOpenChange(false)
    }
  }

  // Message is optional for status_check
  const isMessageRequired = selectedReason !== 'status_check'

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[525px]">
        {submitted ? (
          <div className="py-8 space-y-4 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Thank you!</h3>
              <p className="text-sm text-muted-foreground">
                We've received your inquiry and will get back to you within 24 hours.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>Contact Us</DialogTitle>
              <DialogDescription>
                Let us know if you need faster access or have questions about AionTrack.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="reason">
                  Reason <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={selectedReason}
                  onValueChange={(value) => setValue('reason', value as WaitlistReason)}
                >
                  <SelectTrigger id="reason" aria-invalid={errors.reason ? 'true' : 'false'}>
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {REASON_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.reason && (
                  <p className="text-sm text-destructive">{errors.reason.message}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="message">
                  Message {isMessageRequired && <span className="text-destructive">*</span>}
                  {!isMessageRequired && <span className="text-muted-foreground text-xs">(optional)</span>}
                </Label>
                <Textarea
                  id="message"
                  placeholder="Tell us more..."
                  rows={4}
                  {...register('message')}
                  aria-invalid={errors.message ? 'true' : 'false'}
                />
                {errors.message && (
                  <p className="text-sm text-destructive">{errors.message.message}</p>
                )}
                {!isMessageRequired && (
                  <p className="text-xs text-muted-foreground">
                    For status checks, a message is optional.
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={submitInquiry.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitInquiry.isPending}>
                {submitInquiry.isPending ? 'Submitting...' : 'Submit'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
